import React, { useCallback, useEffect, useState } from 'react';
import {
  MdAssessment,
  MdAutoGraph,
  MdAttachMoney,
  MdDataUsage,
  MdInsights,
  MdRefresh,
  MdRoute,
  MdToken,
} from 'react-icons/md';
import { useCampaignAccess } from '../contexts/CampaignAccessContext';
import { isUsageAdminUser } from '../utils/adminAccess';
import { requestAdminUsageReport } from '../utils/adminUsageClient';
import Header from './Header';
import SideBar from './SideBar';
import '../styles/UsagePage.css';

function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not rated';
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function formatCompact(value) {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

function safePercent(value, total) {
  if (!total) return 0;
  return Math.max(0, Math.min(100, (Number(value || 0) / Number(total)) * 100));
}

function totalSpendFromReport(report) {
  const totals = report?.totals || {};
  const openai = totals.openai || {};
  const graphhopper = totals.graphhopper || {};
  if (totals.totalEstimatedCostUsd !== undefined && totals.totalEstimatedCostUsd !== null) {
    return Number(totals.totalEstimatedCostUsd || 0);
  }
  return Number(openai.totalCostUsd || 0) + Number(graphhopper.estimatedCostUsd || 0);
}

function budgetFromReport(report, totalSpend) {
  const rawBudget = report?.budget?.monthlyBudgetUsd ?? report?.limits?.monthlyBudgetUsd;
  const monthlyBudgetUsd = Number(rawBudget);
  if (!Number.isFinite(monthlyBudgetUsd) || monthlyBudgetUsd <= 0) return null;

  const usedPercent = report?.budget?.usedPercent ?? ((Number(totalSpend || 0) / monthlyBudgetUsd) * 100);
  const remainingUsd = report?.budget?.remainingUsd ?? Math.max(0, monthlyBudgetUsd - Number(totalSpend || 0));
  return {
    monthlyBudgetUsd,
    usedPercent: Math.max(0, Number(usedPercent || 0)),
    remainingUsd: Math.max(0, Number(remainingUsd || 0)),
  };
}

function categoryById(report, id) {
  return (report?.spendCategories || []).find(category => category.id === id) || {};
}

function isDeveloperUsageAccount(user = {}) {
  return user.isDeveloperAccount === true;
}

function usageUserLabel(user = {}) {
  if (isDeveloperUsageAccount(user)) return 'Developer Account';
  return user.displayName || user.email || user.uid || 'Unknown account';
}

function formatShortDate(date) {
  return String(date || '').slice(5) || 'No date';
}

function peakSpendFromReport(report) {
  const series = report?.series || [];
  if (!series.length) return null;

  const peakDay = series.reduce((best, day) => (
    Number(day.totalCostUsd || 0) > Number(best.totalCostUsd || 0) ? day : best
  ), series[0]);
  const categoryLookup = new Map((report?.spendCategories || []).map(category => [category.id, category.label]));
  const categoryEntries = Object.entries(peakDay.categoryCosts || {});
  const topCategory = categoryEntries.length
    ? categoryEntries.reduce((best, entry) => Number(entry[1] || 0) > Number(best[1] || 0) ? entry : best, categoryEntries[0])
    : null;
  const userDayEntries = (report?.users || []).map(user => ({
    user,
    day: (user.daily || []).find(day => day.date === peakDay.date) || {},
  }));
  const topUser = userDayEntries.length
    ? userDayEntries.reduce((best, entry) => (
      Number(entry.day.totalCostUsd || 0) > Number(best.day.totalCostUsd || 0) ? entry : best
    ), userDayEntries[0])
    : null;

  return {
    date: peakDay.date,
    totalCostUsd: Number(peakDay.totalCostUsd || 0),
    topCategoryLabel: topCategory ? (categoryLookup.get(topCategory[0]) || topCategory[0]) : 'No category spike',
    topCategoryCostUsd: topCategory ? Number(topCategory[1] || 0) : 0,
    topUserLabel: topUser ? usageUserLabel(topUser.user) : 'No account spike',
    topUserCostUsd: topUser ? Number(topUser.day.totalCostUsd || 0) : 0,
    graphhopperCredits: Number(peakDay.graphhopperCredits || 0),
  };
}

// `pending` is load-bearing, not cosmetic. Before it existed this tile rendered
// formatCurrency(0) while the report was still in flight, so a real campaign's
// spend read as "$0.00" for the ~25s the query took. A screen that states a
// definite zero it has not read is worse than one that says nothing: the reader
// cannot tell the two apart, and "this cost nothing" is the answer they act on.
function UsageMetric({ icon: Icon, label, value, detail, pending = false }) {
  return (
    <article className="usage-metric">
      <div className="usage-metric__icon" aria-hidden="true">
        <Icon size={22} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{pending ? '—' : value}</strong>
        {pending ? <small>Reading the spend ledger…</small> : (detail && <small>{detail}</small>)}
      </div>
    </article>
  );
}

function EmptyState({ children }) {
  return <p className="usage-page__empty">{children}</p>;
}

const chartColors = ['#14b8a6', '#7c3aed', '#f59e0b', '#0ea5e9', '#ef4444'];

function buildPolyline(values, maxValue) {
  const width = 620;
  const height = 170;
  const pad = 14;
  if (values.length === 1) {
    const y = height - pad - ((Number(values[0] || 0) / maxValue) * (height - pad * 2));
    return `${pad},${y} ${width - pad},${y}`;
  }
  return values.map((value, index) => {
    const x = pad + (index / Math.max(1, values.length - 1)) * (width - pad * 2);
    const y = height - pad - ((Number(value || 0) / maxValue) * (height - pad * 2));
    return `${x},${y}`;
  }).join(' ');
}

function buildChartLines(report, mode, selectedUserId) {
  const series = report?.series || [];
  if (mode === 'company') {
    return [{
      id: 'company',
      label: 'Company total',
      values: series.map(day => Number(day.totalCostUsd ?? day.openaiCostUsd ?? 0)),
    }];
  }

  if (mode === 'work') {
    return (report?.spendCategories || [])
      .filter(category => category.id !== 'other')
      .slice(0, 5)
      .map(category => ({
        id: category.id,
        label: category.label,
        values: series.map(day => Number(day.categoryCosts?.[category.id] || 0)),
      }))
      .filter(line => line.values.some(value => value > 0));
  }

  const users = selectedUserId
    ? (report?.users || []).filter(user => user.uid === selectedUserId)
    : (report?.users || []).slice(0, 5);

  return users.map(user => ({
    id: user.uid,
    label: usageUserLabel(user),
    values: (user.daily || series).map(day => Number(day.totalCostUsd ?? day.openaiCostUsd ?? 0)),
  })).filter(line => line.values.some(value => value > 0));
}

function DailySpendChart({ report, mode, selectedUserId, pending = false }) {
  const lines = pending ? [] : buildChartLines(report, mode, selectedUserId);
  const maxValue = Math.max(1, ...lines.flatMap(line => line.values));
  const firstDate = report?.series?.[0]?.date?.slice(5);
  const lastDate = report?.series?.[report.series.length - 1]?.date?.slice(5);
  const label = mode === 'work'
    ? 'Daily cost by work type'
    : mode === 'company'
      ? 'Daily company cost'
      : 'Daily cost by account';

  return (
    <div className="usage-line-chart" aria-label={label}>
      {lines.length === 0 ? (
        <EmptyState>{pending ? 'Reading the spend ledger…' : 'No spend recorded in this view yet.'}</EmptyState>
      ) : (
        <>
          <svg viewBox="0 0 620 170" role="img" aria-label={label}>
            <line x1="14" y1="156" x2="606" y2="156" className="usage-line-chart__axis" />
            <line x1="14" y1="14" x2="14" y2="156" className="usage-line-chart__axis" />
            {lines.map((line, index) => (
              <polyline
                key={line.id}
                className="usage-line-chart__line"
                points={buildPolyline(line.values, maxValue)}
                style={{ stroke: chartColors[index % chartColors.length] }}
              />
            ))}
          </svg>
          <div className="usage-line-chart__meta">
            <span>{firstDate}</span>
            <strong>Peak day {formatCurrency(maxValue)}</strong>
            <span>{lastDate}</span>
          </div>
          <div className="usage-line-chart__legend">
            {lines.map((line, index) => (
              <span key={line.id}>
                <i style={{ background: chartColors[index % chartColors.length] }} />
                {line.label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SpendSpikePanel({ report }) {
  const peak = peakSpendFromReport(report);

  return (
    <article className="usage-panel usage-panel--spike">
      <div className="usage-panel__header">
        <div>
          <span>Spend spike lens</span>
          <h3>What caused the peak</h3>
        </div>
        <MdInsights size={20} />
      </div>
      {!peak ? (
        <EmptyState>No daily spend spike in this range yet.</EmptyState>
      ) : (
        <div className="usage-spike-list">
          <div>
            <span>Peak spend day</span>
            <strong>{formatShortDate(peak.date)}</strong>
            <small>{formatCurrency(peak.totalCostUsd)} total</small>
          </div>
          <div>
            <span>Main driver</span>
            <strong>{peak.topCategoryLabel}</strong>
            <small>{formatCurrency(peak.topCategoryCostUsd)} that day</small>
          </div>
          <div>
            <span>Top account</span>
            <strong>{peak.topUserLabel}</strong>
            <small>{formatCurrency(peak.topUserCostUsd)} that day</small>
          </div>
          <div>
            <span>Map pressure</span>
            <strong>{formatNumber(peak.graphhopperCredits)} credits</strong>
            <small>geocoding and road timing demand</small>
          </div>
        </div>
      )}
    </article>
  );
}

function MonthlyBudgetCard({ report, totalSpend, budget, pending = false }) {
  const rangeLabel = pending
    ? 'Reading the spend ledger…'
    : (report?.range ? `${report.range.startDate} to ${report.range.endDate}` : 'Current month');
  const percent = budget ? Math.round(budget.usedPercent) : null;

  return (
    <section className="usage-budget-card" aria-label="Monthly budget summary">
      <div>
        <p className="usage-page__eyebrow">Tracked spend control center</p>
        <h3>{pending
          ? '—'
          : (budget ? `${formatCurrency(totalSpend)} of ${formatCurrency(budget.monthlyBudgetUsd)}` : formatCurrency(totalSpend))}</h3>
        <p>
          {pending
            ? 'Spend for this range has not been read yet.'
            : (budget
              ? `${percent}% of the tracked monthly budget used. ${formatCurrency(budget.remainingUsd)} remains for this month.`
              : 'No monthly budget has been set yet. This view still tracks provider spend by user and work type.')}
        </p>
      </div>
      <div className="usage-budget-card__meter" aria-hidden="true">
        <span style={{ width: `${pending ? 0 : Math.min(100, budget ? budget.usedPercent : 0)}%` }} />
      </div>
      <small>{rangeLabel}</small>
    </section>
  );
}

function TokenCostBreakdown({ items = [] }) {
  const maxCost = Math.max(1, ...items.map(item => Number(item.costUsd || 0)));
  return (
    <div className="usage-token-bars">
      {items.map(item => (
        <div className="usage-token-bars__item" key={item.id}>
          <div>
            <strong>{item.label}</strong>
            <span>{formatNumber(item.tokens)} tokens</span>
          </div>
          <div className="usage-token-bars__track" aria-hidden="true">
            <div style={{ width: `${safePercent(item.costUsd, maxCost)}%` }} />
          </div>
          <strong>{formatCurrency(item.costUsd)}</strong>
        </div>
      ))}
    </div>
  );
}

export default function UsagePage() {
  const campaignAccess = useCampaignAccess();
  const isAdmin = isUsageAdminUser(campaignAccess);
  const [rangeMode, setRangeMode] = useState('month_to_date');
  const [userId, setUserId] = useState('');
  const [trendMode, setTrendMode] = useState('users');
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    setError('');
    try {
      const rangePayload = rangeMode === 'month_to_date'
        ? { rangePreset: 'month_to_date' }
        : { rangeDays: Number(rangeMode) };
      const nextReport = await requestAdminUsageReport({ ...rangePayload, userId });
      setReport(nextReport);
    } catch (err) {
      setError(err?.message || 'Usage report could not be loaded.');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, rangeMode, userId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // One derived flag rather than four ad-hoc checks: every figure on this page
  // is either read or it is not, and they must all say the same thing.
  const isPending = isLoading && !report;

  const totals = report?.totals || {};
  const openai = totals.openai || {};
  const graphhopper = totals.graphhopper || {};
  const totalSpend = totalSpendFromReport(report);
  const conversationCategory = categoryById(report, 'conversation');
  const locationCategory = categoryById(report, 'location');
  const locationCost = locationCategory.costUsd ?? graphhopper.estimatedCostUsd;
  const budget = budgetFromReport(report, totalSpend);

  if (!isAdmin) {
    return (
      <div className="app-layout">
        <SideBar />
        <main className="main-content usage-page">
          <section className="usage-page__denied" role="alert">
            <h2>Usage is admin-only</h2>
            <p>This page is restricted to the SiteSift admin accounts.</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <SideBar />
      <main className="main-content usage-page">
        <Header
          stats={[
            { label: 'Tracked Spend', value: formatCurrency(totalSpend), loading: isLoading && !report },
            { label: 'GPT Spend', value: formatCurrency(openai.totalCostUsd), loading: isLoading && !report },
            { label: 'Map Credits', value: graphhopper.creditsUsed || 0, loading: isLoading && !report },
          ]}
        />
        <section className="usage-page__hero">
          <div>
            <p className="usage-page__eyebrow">Admin controls</p>
            <h2>Usage</h2>
            <p>
              Watch tracked provider spend by user, day, and product workflow without exposing broker emails or prompt text.
            </p>
          </div>
          <div className="usage-page__filters" aria-label="Usage filters">
            <label>
              <span>Date range</span>
              <select value={rangeMode} onChange={event => setRangeMode(event.target.value)}>
                <option value="month_to_date">Month to date</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="366">Last year</option>
              </select>
            </label>
            <label>
              <span>User filter</span>
              <select value={userId} onChange={event => setUserId(event.target.value)}>
                <option value="">All users</option>
                {(report?.users || []).map(user => (
                  <option key={user.uid} value={user.uid}>
                    {usageUserLabel(user)}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={loadReport} disabled={isLoading}>
              <MdRefresh size={18} />
              {isLoading ? 'Refreshing' : 'Refresh usage'}
            </button>
          </div>
        </section>

        {error && <div className="usage-page__error" role="alert">{error}</div>}

        <MonthlyBudgetCard report={report} totalSpend={totalSpend} budget={budget} pending={isPending} />

        <section className="usage-page__metrics" aria-label="Usage totals">
          <UsageMetric
            icon={MdAttachMoney}
            label="Tracked provider spend"
            value={formatCurrency(totalSpend)}
            detail={report?.range ? `${report.range.startDate} to ${report.range.endDate}` : 'Current month'}
            pending={isPending}
          />
          <UsageMetric
            icon={MdToken}
            label="GPT token spend"
            value={formatCurrency(openai.totalCostUsd)}
            detail={`${formatCompact(openai.inputTokens)} input / ${formatCompact(openai.outputTokens)} output tokens`}
            pending={isPending}
          />
          <UsageMetric
            icon={MdAutoGraph}
            label="Conversation handling"
            value={formatCurrency(conversationCategory.costUsd)}
            detail={`${formatNumber(conversationCategory.calls)} AI steps for replies and follow-ups`}
            pending={isPending}
          />
          <UsageMetric
            icon={MdRoute}
            label="Map and tour spend"
            value={formatCurrency(locationCost)}
            detail={`${formatNumber(graphhopper.creditsUsed)} credits for geocoding and routing`}
            pending={isPending}
          />
        </section>

        <section className="usage-page__grid">
          <article className="usage-panel usage-panel--wide">
            <div className="usage-panel__header">
              <div>
                <span>Month trend</span>
                <h3>{trendMode === 'work' ? 'Daily cost by work type' : trendMode === 'company' ? 'Daily company cost' : 'Daily cost by account'}</h3>
              </div>
              {report?.range && <small>{report.range.startDate} to {report.range.endDate}</small>}
            </div>
            <div className="usage-view-toggle" aria-label="Spend chart view">
              <button type="button" className={trendMode === 'users' ? 'active' : ''} onClick={() => setTrendMode('users')}>
                User spend
              </button>
              <button type="button" className={trendMode === 'work' ? 'active' : ''} onClick={() => setTrendMode('work')}>
                Work type spend
              </button>
              <button type="button" className={trendMode === 'company' ? 'active' : ''} onClick={() => setTrendMode('company')}>
                Company spend
              </button>
            </div>
            <DailySpendChart report={report} mode={trendMode} selectedUserId={userId} pending={isPending} />
          </article>

          <SpendSpikePanel report={report} />

          <article className="usage-panel usage-panel--wide">
            <div className="usage-panel__header">
              <div>
                <span>Work categories</span>
                <h3>What the money is being spent on</h3>
              </div>
              <MdDataUsage size={20} />
            </div>
            <div className="usage-category-grid">
              {(report?.spendCategories || []).map(category => (
                <div className="usage-category-card" key={category.id}>
                  <div>
                    <strong>{category.label}</strong>
                    <p>{category.description}</p>
                  </div>
                  <div>
                    <strong>{formatCurrency(category.costUsd)}</strong>
                    <span>{formatNumber(category.calls)} AI calls{category.graphhopperCredits ? ` · ${formatNumber(category.graphhopperCredits)} map credits` : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="usage-panel">
            <div className="usage-panel__header">
              <div>
                <span>Users</span>
                <h3>Cost by account</h3>
              </div>
              <MdInsights size={20} />
            </div>
            {(report?.users || []).length === 0 ? (
              <EmptyState>No usage recorded in this range.</EmptyState>
            ) : (
              <div className="usage-list">
                {report.users.map(user => (
                  <div className="usage-list__item" key={user.uid}>
                    <div>
                      <strong>{usageUserLabel(user)}</strong>
                      <span>{user.email || user.uid}</span>
                    </div>
                    <div>
                      <strong>{formatCurrency(user.totalEstimatedCostUsd)}</strong>
                      <span>{formatNumber(user.graphhopper?.creditsUsed)} map credits</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="usage-panel">
            <div className="usage-panel__header">
              <div>
                <span>Token cost</span>
                <h3>GPT spend split</h3>
              </div>
              <MdToken size={20} />
            </div>
            <TokenCostBreakdown items={report?.tokenCostBreakdown || []} />
          </article>

          <article className="usage-panel">
            <div className="usage-panel__header">
              <div>
                <span>Models</span>
                <h3>GPT mix</h3>
              </div>
              <MdAssessment size={20} />
            </div>
            {(report?.models || []).length === 0 ? (
              <EmptyState>No model usage yet.</EmptyState>
            ) : (
              <div className="usage-list">
                {report.models.map(model => (
                  <div className="usage-list__item" key={model.model}>
                    <div>
                      <strong>{model.model}</strong>
                      <span>{formatNumber(model.calls)} calls</span>
                    </div>
                    <div>
                      <strong>{formatCurrency(model.costUsd)}</strong>
                      <span>{formatNumber(model.totalTokens)} tokens</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="usage-panel">
            <div className="usage-panel__header">
              <div>
                <span>Operations</span>
                <h3>Feature cost</h3>
              </div>
            </div>
            {(report?.operations || []).length === 0 ? (
              <EmptyState>No operation usage yet.</EmptyState>
            ) : (
              <div className="usage-list">
                {report.operations.map(operation => (
                  <div className="usage-list__item" key={operation.operation}>
                    <div>
                      <strong>{operation.operation}</strong>
                      <span>{formatNumber(operation.calls)} calls</span>
                    </div>
                    <strong>{formatCurrency(operation.costUsd)}</strong>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="usage-panel">
            <div className="usage-panel__header">
              <div>
                <span>Campaigns</span>
                <h3>Top campaign spend</h3>
              </div>
            </div>
            {(report?.campaigns || []).length === 0 ? (
              <EmptyState>No campaign-level usage in this range.</EmptyState>
            ) : (
              <div className="usage-list">
                {report.campaigns.map(campaign => (
                  <div className="usage-list__item" key={`${campaign.uid}-${campaign.clientId}`}>
                    <div>
                      <strong>{campaign.clientName}</strong>
                      <span>{usageUserLabel({
                        uid: campaign.uid,
                        email: campaign.userEmail,
                        displayName: campaign.userDisplayName,
                        isDeveloperAccount: campaign.isDeveloperAccount,
                      })}</span>
                    </div>
                    <div>
                      <strong>{formatCurrency(campaign.openai?.totalCostUsd)}</strong>
                      <span>{formatNumber(campaign.openai?.calls)} GPT calls</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>
        </section>

        <section className="usage-page__guardrails">
          <h3>What this tracks</h3>
          <div>
            <p>GPT telemetry uses stored token/cost rollups. Prompts, broker emails, PDF text, and message bodies are not shown here.</p>
            <p>Map provider telemetry shows paid credits for geocoding and fallback routing pressure. Dollar cost is included only when per-credit pricing is configured.</p>
            <p>Infrastructure costs from Firebase, Cloud Run, Cloud Tasks, Microsoft Graph, and Google Sheets are outside this view.</p>
            {report?.coverage?.gaps?.map(gap => <p key={gap}>{gap}</p>)}
          </div>
        </section>
      </main>
    </div>
  );
}
